import { RefObject, useEffect, useRef, useState } from "react";

/**
 * Tracks the width of an element instead of the viewport. Screens that render inside the
 * portal shell only get `innerWidth` minus the sidenav and content padding, so viewport
 * breakpoints would select a layout wider than the space actually available.
 *
 * Starts from `window.innerWidth` so the first paint has a sensible guess, then corrects to
 * the measured width once mounted.
 */
const useContainerWidth = <T extends HTMLElement>(): [RefObject<T>, number] => {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1440);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // A zero measurement means the node isn't laid out (jsdom, display:none) — keep the guess.
    const measure = () => {
      const next = node.getBoundingClientRect().width;
      if (next > 0) setWidth(next);
    };
    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure, { passive: true });
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
};

export default useContainerWidth;
